'use client'

import { AuthorizationContext } from '@entities/authentication/model/authorization.context'
import { userEntityQueries } from '@entities/users'
import { useI18n } from '@shared/config/i18n'
import { useQuery } from '@tanstack/react-query'
import { type ComponentProps, useContext, useId, useMemo } from 'react'

import { BottomSheetContent, BottomSheetHeader, Button, Typography } from '@podo-app/design-system-temp'

import { fetchPublicPaymentTerms, selectTermsCountry } from './public-payment-terms'
import { sanitizePaymentTerms } from './sanitize-payment-terms'

import styles from './public-payment-terms.module.css'

/** Rendered inside the portal: closed sheets do not request profiles or terms. */
function PublicPaymentTerms() {
  const authorization = useContext(AuthorizationContext)
  const isAuthorized = authorization?.isAuthorized === true
  const { languageCode, systemLocale, t } = useI18n()
  const user = useQuery({
    ...userEntityQueries.getCurrentUser({ bearerToken: isAuthorized ? authorization.bearerToken : '' }),
    enabled: isAuthorized,
    retry: false,
  })
  const country = selectTermsCountry({
    isAuthorized,
    countryCode: user.data?.countryCode,
    languageCode,
    systemLocale,
  })
  const terms = useQuery({
    queryKey: ['public-payment-terms', country],
    queryFn: ({ signal }) => {
      if (!country) {
        throw new Error('Terms country not resolved')
      }
      return fetchPublicPaymentTerms(country, signal)
    },
    enabled: country !== null && (!isAuthorized || !user.isError),
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    retry: false,
  })
  const html = useMemo(
    () => (terms.data && country ? sanitizePaymentTerms(terms.data.content, country) : ''),
    [terms.data, country],
  )
  const isProfilePending = isAuthorized && user.isPending
  const isLoading =
    !(isAuthorized && user.isError) && (isProfilePending || (country !== null && terms.isPending && !terms.isError))

  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-10" role="status" aria-live="polite">
        <span className={styles.spinner} aria-hidden="true" />
        <p>{t('payment.terms.loading')}</p>
      </div>
    )
  }
  if (!country || (isAuthorized && user.isError) || terms.isError || !html.replace(/<[^>]*>/g, '').trim()) {
    return (
      <div className="flex flex-col items-center gap-4 py-10" role="alert">
        <p>{t('payment.terms.loadFailed')}</p>
        <Button
          type="button"
          variant="ghost"
          onClick={() => {
            if (isAuthorized && (!country || user.isError)) {
              void user.refetch()
            } else {
              void terms.refetch()
            }
          }}
        >
          {t('common.actions.retry')}
        </Button>
      </div>
    )
  }
  return (
    <div className={styles.body} lang={country === 'JP' ? 'ja' : 'ko'} dangerouslySetInnerHTML={{ __html: html }} />
  )
}

export function PaymentTermsInformationBottomSheet(props: ComponentProps<typeof BottomSheetContent>) {
  const { children, ...contentProps } = props
  const { t } = useI18n()
  const titleId = useId()
  return (
    <BottomSheetContent size="full" aria-labelledby={titleId} {...contentProps}>
      <BottomSheetHeader>
        <Typography asChild size="h1">
          <h2 id={titleId}>{t('payment.terms.title')}</h2>
        </Typography>
      </BottomSheetHeader>
      <div className="px-5 pt-4 pb-[15px] text-sm text-gray-900">
        <PublicPaymentTerms />
        {children}
      </div>
    </BottomSheetContent>
  )
}
